/**
 * SelectTextQuiz Student Renderer
 * Student-style UI matching legacy SelectedText2 with inline bottom panel.
 */

import { useMemo, memo, ReactNode, useCallback } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText, SubmitButton, QuizSolveDescription, QuizHeader, QuizResultToggle, type QuizResultToggleValue } from '../shared'
import { formatProblem } from '../../../utils/quiz-parser'
import type { SelectTextQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { parseSelectTextContents, parseSelectTextOptionModels, useSelectTextQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'

interface SelectTextStudentProps {
  quizId: string | number
  quiz: SelectTextQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용/문제 이미지를 흐린다. */
  dimChoiceText?: boolean
}

// 개별 선택 영역 컴포넌트 (버튼만, 패널은 부모에서 렌더링)
interface SelectBlankProps {
  quizId: string | number
  index: number
  content: string
  selectedOptionIdx: number | null
  isOpen: boolean
  isSolved: boolean
  isViewSolved: boolean
  isCorrectAnswer: boolean
  shouldShowCorrectAnswers: boolean
  correctAnswerIndex: number | null
  onToggle: (index: number) => void
}

const StudentSelectBlank = memo(function StudentSelectBlank({
  quizId,
  index,
  content,
  selectedOptionIdx,
  isOpen,
  isSolved,
  isViewSolved,
  isCorrectAnswer,
  shouldShowCorrectAnswers,
  correctAnswerIndex,
  onToggle,
}: SelectBlankProps) {
  const { t } = useTranslation('quiz')

  const options = useMemo(() => {
    return parseSelectTextOptionModels(content)
  }, [content])

  const selectedOption = selectedOptionIdx !== null && options[selectedOptionIdx]?.isSelectable
    ? options[selectedOptionIdx]
    : null
  const showAsCorrect = isSolved && isCorrectAnswer
  const showAsWrong = isSolved && !isCorrectAnswer && !shouldShowCorrectAnswers
  const showCorrectOverride = shouldShowCorrectAnswers && isSolved && !isCorrectAnswer
  const correctAnswer = options[correctAnswerIndex ?? 0]?.label ?? options.find(option => option.isSelectable)?.label ?? ''

  const isCompleted = isSolved || isViewSolved
  const hasValue = selectedOption !== null

  return (
    <span key={`select-${quizId}-${index}`} className="inline-block my-1">
      <button
        onClick={(e) => {
          e.stopPropagation()
          if (!isCompleted) onToggle(index)
        }}
        disabled={isCompleted}
        className={cn(
          'rounded-2xl mx-2 font-medium inline-flex items-center justify-center gap-2 px-4 h-12 text-[24px]',
          // 완료 상태 (정답/오답/비활성)
          showCorrectOverride && 'bg-lc-mint-200 text-lc-mint-900 border-none [box-shadow:unset] pointer-events-none',
          showAsCorrect && !showCorrectOverride && 'bg-lc-mint-200 text-lc-mint-900 border-none [box-shadow:unset] pointer-events-none',
          showAsWrong && 'bg-lc-red-200 text-lc-red-900 border-none [box-shadow:unset] pointer-events-none',
          isViewSolved && !isSolved && 'border-none [box-shadow:unset] pointer-events-none',
          // 미완료 상태
          !isCompleted && !isOpen && !hasValue && 'border-2 border-lc-gray-400 [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]',
          !isCompleted && isOpen && 'border-2 border-lc-lightblue-900 bg-lc-lightblue-200 min-w-[96px]',
          !isCompleted && !isOpen && hasValue && 'border-2 border-lc-lightblue-900 bg-lc-lightblue-200',
        )}
      >
        <span className={cn(isOpen && !hasValue && 'invisible')}>
          {showCorrectOverride
            ? <KatexText text={correctAnswer} />
            : hasValue
              ? <KatexText text={selectedOption.label} />
              : t('types.selectText.placeholder')}
        </span>
      </button>
    </span>
  )
})

export function SelectTextStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: SelectTextStudentProps) {
  const { t } = useTranslation('quiz')

  const {
    answers,
    currentIndex,
    openDropdown,
    selectCount,
    problemKey,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggleDropdown,
    handleSelect,
    handleSubmit,
  } = useSelectTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // 모든 빈칸의 옵션 추출
  const allBlankOptions = useMemo(() => {
    return parseSelectTextContents(quiz.problem).map(parseSelectTextOptionModels)
  }, [quiz.problem])

  // 현재 열린 드롭다운의 옵션
  const currentOptions = openDropdown !== null ? allBlankOptions[openDropdown] ?? [] : []
  const currentSelectedIdx = openDropdown !== null && answers[openDropdown] !== '' ? parseInt(answers[openDropdown]) : null

  const handlePanelSelect = useCallback((optIdx: number) => {
    if (openDropdown !== null) {
      handleSelect(openDropdown, optIdx)
    }
  }, [openDropdown, handleSelect])

  // 문제 렌더링
  const renderedProblem = useMemo<ReactNode[]>(() => {
    if (answers.length === 0 && selectCount > 0) return []

    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${problemKey}`,
      (index: number, content: string) => {
        const selectedOptionIdx = answers[index] !== '' ? parseInt(answers[index]) : null
        const isOpen = openDropdown === index

        // 정답 여부 확인
        const isCorrectByCorrects = solveResult?.corrects?.[index]
        const isCorrectAnswer = (isCorrectByCorrects !== undefined && isCorrectByCorrects !== null) ? isCorrectByCorrects : selectedOptionIdx === 0

        // 서버에서 받은 정답 인덱스
        const correctAnswerFromServer = solveResult?.correctAnswers?.[index]
        const correctAnswerIndex = typeof correctAnswerFromServer === 'number'
          ? correctAnswerFromServer
          : (typeof correctAnswerFromServer === 'string' ? parseInt(correctAnswerFromServer) : null)

        return (
          <StudentSelectBlank
            key={`select-${quizId}-${problemKey}-${index}`}
            quizId={quizId}
            index={index}
            content={content}
            selectedOptionIdx={selectedOptionIdx}
            isOpen={isOpen}
            isSolved={isSolved}
            isViewSolved={isViewSolved}
            isCorrectAnswer={isCorrectAnswer}
            shouldShowCorrectAnswers={shouldShowCorrectAnswers}
            correctAnswerIndex={correctAnswerIndex}
            onToggle={handleToggleDropdown}
          />
        )
      },
      quiz.additionalImages ?? undefined
    )
  }, [answers, currentIndex, openDropdown, isSolved, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, selectCount, quiz.additionalImages, solveResult?.corrects, solveResult?.correctAnswers, problemKey, handleToggleDropdown])

  const isCompleted = isSolved || isViewSolved
  const panelOpen = openDropdown !== null && !isCompleted

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.selectText.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimMedia={dimChoiceText}
      />

      {/* 문제 본문 - legacy: text-headline-4 leading-[48px] */}
      <div
        data-quiz-problem-text
        className={cn(
          'text-2xl font-medium text-gray-700 leading-[48px]',
          dimChoiceText && '[&_.formatProblem]:opacity-[0.05] [&_.formatProblem_other]:opacity-[0.05]'
        )}
      >
        {renderedProblem}
      </div>

      {/* 인라인 옵션 패널 - 문제 아래에 표시 */}
      <AnimatePresence>
        {panelOpen && currentOptions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
            className="w-full flex items-center justify-center gap-6 px-4"
          >
            {currentOptions.map((option) => {
              if (!option.isSelectable) return null
              const isSelected = currentSelectedIdx === option.rawIndex
              return (
                <button
                  key={option.rawIndex}
                  onClick={() => handlePanelSelect(option.rawIndex)}
                  className={cn(
                    'h-[92px] text-[20px] text-center overflow-hidden font-medium flex flex-wrap bg-white justify-center items-center border-2 rounded-3xl grow cursor-pointer select-none py-4 px-6',
                    isSelected
                      ? 'text-lc-lightblue-900 border-lc-lightblue-900 [box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]'
                      : 'text-gray-900 border-lc-gray-400 [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]'
                  )}
                >
                  <KatexText text={option.label} />
                </button>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
